import { makeObservable, observable } from "mobx";
import { LogUser, UserActivityLog } from "core/services/user-activity-logging.service";
import PagerStore from "core/stores/pager.store";
import { direction, fields } from "./user-activity-logger.store.manager";

const secondarySort = {
    "datetime": null,
    "name": fields.dateTime,
    "usertype": fields.name,
    "action": fields.name
}

export const logName = (log: UserActivityLog) => {
    const name = `${log.firstName} ${log.lastName}`.split("null").join(" ").trim();
    return name.length === 0
        ? log.email.length === 0
            ? log.id : log.email
        : name;
}

const compareStrings = (a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase());

const compare = (field: string, a: UserActivityLog, b: UserActivityLog): number => {
    const key = field as keyof typeof secondarySort;
    const field2 = secondarySort[key];

    switch (field) {
        case fields.dateTime:
            if (a.dateCreated < b.dateCreated) {
                return -1;
            }

            if (a.dateCreated > b.dateCreated) {
                return 1;
            }

            if (field2 != null) {
                return compare(field2, a, b);
            }

            return 0;

        case fields.action:
            return compareStrings(a.description, b.description);

        case fields.name:
            return compareStrings(logName(a), logName(b));

        case fields.userType:
            return compareStrings(a.userType, b.userType);

        default:
            return 0;
    }
}

const sortLogs = (logs: UserActivityLog[], field: string, reverse = false) => {
    const sortFunction = (a: UserActivityLog, b: UserActivityLog) => compare(field, a, b);

    logs.sort(sortFunction);

    if (reverse) {
        logs.reverse();
    }

    return logs;
}

type Sorting = {
    field: string,
    direction: string,
    hasFieldChanged: boolean
}

class UserActivityLogStore extends PagerStore<UserActivityLog> {
    hasError = false;
    isLoading = false;
    filter = "";
    users: LogUser[] = [];

    sorting: Sorting = {
        field: fields.dateTime,
        direction: direction.desc,
        hasFieldChanged: false
    };

    constructor() {
        super();

        this.itemsPerPage = 10;

        makeObservable(this, {
            hasError: observable,
            isLoading: observable,
            users: observable,
            filter: observable,
            sorting: observable
        });
    }

    private _onUpdate(items: UserActivityLog[]) {
        this._numberOfItems = items?.length ?? 0;
        this.numPages = this._numberOfItems > 0 ? Math.ceil(this._numberOfItems / this.itemsPerPage) : 0;
    }

    updateSorting(value: Sorting) {
        const hasFieldChanged = value.field !== this.sorting.field;

        this.sorting.field = value.field;
        this.sorting.direction = value.direction;
        this.sorting.hasFieldChanged = hasFieldChanged;

        const logs = this.items;

        if (hasFieldChanged) {
            sortLogs(logs, value.field);
        }

        if (!hasFieldChanged || (hasFieldChanged && value.direction === direction.desc)) {
            logs.reverse();
        }

        this.updateItems(logs);
    }

    updateIsLoading(value: boolean) {
        this.isLoading = value;
    }

    updateUsers(value: LogUser[]) {
        this.users = value;
    }

    updateItems(value: UserActivityLog[]) {
        this.items = value;

        this._onUpdate(this.items);

        this.hasLoaded = true;

        this.updateCurrentPage(0);
    }

    updateErrorLoading(value: boolean) {
        this.hasError = value;
    }

    get hasItems() {
        return this.hasLoaded && this.items.length > 0;
    }

    reset() {
        super.reset();
        this.filter = "";
    }
}

export default UserActivityLogStore;
